import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, DropdownMenu, Icon, cn, type MenuItem } from '@forecastify/ui';
import {
  CONTROL_PANEL_PATHS,
  operatorRosterResponseSchema,
  type OperatorRosterEntry,
} from '@forecastify/shared';
import { useI18n, useT, type MessageKey } from '../i18n';
import { useOperatorSession } from '../session';
import { ActionDialog, DialogField } from '../chrome/action-dialog';
import { formatDate, formatDateTime } from '../chrome/format';
import { HeaderActions } from '../chrome/page-chrome';
import { useToast } from '../chrome/toasts';
import { BTN_SM, FIELD, PILL, TH, TH_ROW, TONE } from '../chrome/tones';
import { SkeletonRows } from '../chrome/skeleton-rows';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Operátorok* — who may open this console, and the second factor each has. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THREE STATES FROM TWO COLUMNS, AND THE MIDDLE ONE IS WHY THE SCREEN HAS A STATE AT ALL.**
 * *Regisztrációra vár* is an active operator with no verified factor — who cannot sign in, because
 * the factor is mandatory with no grace period. The server sends the two facts; the word is derived
 * here (CLAUDE.md rule 8: no invented enum).
 *
 * ⚠️⚠️ **YOUR OWN ROW KEEPS ITS MENU, AND THE MENU SAYS WHY TWO ITEMS ARE MISSING** — the
 * redesign's *"Magadat nem függesztheted fel és nem törölheted."* label under a separator. The first
 * version hid the menu outright, which explained nothing. The database refuses the two verbs anyway
 * (`0170`'s `that_is_you`); this is the UX half of a rule enforced twice.
 *
 * ⚠️⚠️ **NO "RESET SECOND FACTOR" ITEM, WHICH THE MOCKUP DRAWS** — `decisions.md` S-19: clearing
 * somebody's factor turns their account back into a password-only one, which is exactly what an
 * attacker holding a session would want, so it stays `scripts/superadmin.mjs reset-factor`.
 *
 * ⚠️ **Removing somebody asks for their address, typed** — the mockup's proof for the one
 * irreversible verb on this screen. Pasting it proves nothing, so pasting is refused.
 */

type Load =
  | { readonly state: 'loading' }
  | { readonly state: 'failed' }
  | { readonly state: 'ready'; readonly rows: readonly OperatorRosterEntry[] };

type Standing = 'active' | 'invited' | 'suspended';

function standingOf(entry: OperatorRosterEntry): Standing {
  if (entry.status === 'disabled') return 'suspended';
  return entry.enrolled ? 'active' : 'invited';
}

const STANDING_LABEL: Readonly<Record<Standing, MessageKey>> = {
  active: 'operators.state.active',
  invited: 'operators.state.invited',
  suspended: 'operators.state.suspended',
};

/** The mockup's `OS` table, tokens per S-17. */
const STANDING_TONE: Readonly<Record<Standing, string>> = {
  active: TONE.ok,
  invited: TONE.primary,
  suspended: TONE.warn,
};

type Dialog =
  | { readonly kind: 'invite' }
  | { readonly kind: 'suspend' | 'delete'; readonly row: OperatorRosterEntry }
  | null;

export function OperatorsScreen(): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();
  const toast = useToast();

  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [typed, setTyped] = useState('');

  const read = useCallback(async (): Promise<void> => {
    const result = await session.request<unknown>(CONTROL_PANEL_PATHS.operators);
    if (!result.ok) {
      if (result.status !== 401) setLoad({ state: 'failed' });
      return;
    }
    const parsed = operatorRosterResponseSchema.safeParse(result.value);
    setLoad(parsed.success ? { state: 'ready', rows: parsed.data.data } : { state: 'failed' });
  }, [session]);

  useEffect(() => {
    void read();
  }, [read]);

  /** One message per code the roster endpoints can answer with. Rule 5: never on `message`. */
  const messageFor = (code: string): string => {
    const known: Readonly<Record<string, MessageKey>> = {
      not_found: 'error.not_found',
      that_is_you: 'operators.error.thatIsYou',
      last_operator: 'operators.error.lastOperator',
      already_an_operator: 'operators.error.alreadyAnOperator',
      address_belongs_to_a_member: 'operators.error.belongsToAMember',
      already_suspended: 'error.already_suspended',
      already_active: 'error.already_active',
      validation_failed: 'error.validation_failed',
      unauthenticated: 'error.unauthenticated',
      unreachable: 'error.unreachable',
    };
    const key = known[code];
    return key === undefined ? t('error.unknown') : t(key);
  };

  /**
   * One shape for every write: send, re-read the roster, toast, or report a refusal by code.
   *
   * ⚠️ **Re-read, not patched**: one person's suspension can change what is offered on somebody
   * else's row (`another_operator_can_sign_in`), and a patched copy would be wrong about the rest.
   */
  const write = (
    path: string,
    method: 'POST' | 'DELETE',
    body: unknown,
    done: { title: string; description: string },
  ): void => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    void session
      .request<unknown>(path, {
        method,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      })
      .then((result) => {
        setBusy(false);
        if (!result.ok) {
          if (result.status !== 401) setFailure(messageFor(result.code));
          return;
        }
        setDialog(null);
        toast(done);
        void read();
      });
  };

  const openInvite = (): void => {
    setName('');
    setEmail('');
    setFailure(null);
    setDialog({ kind: 'invite' });
  };

  const inviteOk = name.trim().length > 1 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email.trim());

  return (
    <div data-screen="operators" className="flex flex-col gap-3 p-5">
      <HeaderActions>
        <Button size="sm" className={BTN_SM} onClick={openInvite}>
          <Icon name="mail" size={14} />
          {t('operators.invite')}
        </Button>
      </HeaderActions>

      <p className="text-muted-foreground max-w-[80ch] text-[12.5px] text-pretty">
        {t('operators.lede')}
      </p>

      {failure !== null && dialog === null ? (
        <p role="alert" className="text-destructive text-[12.5px]">
          {failure}
        </p>
      ) : null}

      {load.state === 'failed' ? (
        <div role="alert" className="flex flex-col items-start gap-2">
          <p className="text-[13px]">{t('operators.failed')}</p>
          <Button
            variant="outline"
            size="sm"
            className={BTN_SM}
            onClick={() => {
              void read();
            }}
          >
            {t('tenants.retry')}
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table aria-label={t('operators.heading')} className="w-full min-w-[860px] text-[13px]">
            <thead className="bg-muted/40 border-b">
              <tr className={TH_ROW}>
                <th className={TH}>{t('operators.column.person')}</th>
                <th className={TH}>{t('operators.column.state')}</th>
                <th className={TH}>{t('operators.column.factor')}</th>
                <th className={TH}>{t('operators.column.lastSeen')}</th>
                <th className={cn(TH, 'w-[52px]')}>
                  <span className="sr-only">{t('operators.column.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {load.state === 'loading' ? (
                <SkeletonRows columns={5} rows={3} rowClassName="h-12" />
              ) : (
                load.rows.map((row) => {
                  const standing = standingOf(row);
                  return (
                    <tr key={row.id} className="h-12 border-b last:border-0">
                      <td className="px-3">
                        <div className="flex items-center gap-2.5">
                          <span className="bg-muted grid size-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold">
                            {initials(row.name ?? row.email)}
                          </span>
                          <span className="min-w-0">
                            <span className="flex items-center gap-1.5 font-medium">
                              {row.name ?? row.email}
                              {row.isYou ? (
                                <span className="bg-primary/10 text-primary rounded px-1 text-[10.5px] font-medium">
                                  {t('operators.you')}
                                </span>
                              ) : null}
                            </span>
                            <span className="text-muted-foreground block text-[12px]">
                              {row.email}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className="px-3">
                        <span className={cn(PILL, STANDING_TONE[standing])}>
                          {t(STANDING_LABEL[standing])}
                        </span>
                      </td>
                      <td className={cn('px-3 text-[12.5px]', row.enrolled ? '' : 'text-primary')}>
                        {row.enrolled
                          ? row.enrolledAt === null
                            ? t('operators.factor.enrolled')
                            : t('operators.factor.enrolledOn', {
                                date: formatDate(row.enrolledAt, locale),
                              })
                          : t('operators.factor.none')}
                      </td>
                      <td className="text-muted-foreground px-3 text-[12.5px] whitespace-nowrap tabular-nums">
                        {row.lastSeenAt === null
                          ? t('operators.never')
                          : formatDateTime(row.lastSeenAt, locale, {
                              today: t('when.today'),
                              yesterday: t('when.yesterday'),
                            })}
                      </td>
                      <td className="px-3 text-right">
                        <DropdownMenu
                          items={menuFor(row, standing, t, {
                            reset: () => {
                              write(
                                CONTROL_PANEL_PATHS.operatorPasswordReset(row.id),
                                'POST',
                                undefined,
                                {
                                  title:
                                    standing === 'invited'
                                      ? t('operators.done.resent')
                                      : t('operators.done.reset'),
                                  description: row.email,
                                },
                              );
                            },
                            suspend: () => {
                              setFailure(null);
                              setDialog({ kind: 'suspend', row });
                            },
                            activate: () => {
                              write(
                                CONTROL_PANEL_PATHS.activateOperator(row.id),
                                'POST',
                                undefined,
                                {
                                  title: t('operators.done.activated'),
                                  description: row.name ?? row.email,
                                },
                              );
                            },
                            remove: () => {
                              setTyped('');
                              setFailure(null);
                              setDialog({ kind: 'delete', row });
                            },
                          })}
                          label={t('operators.column.actions')}
                          width={260}
                          align="end"
                        >
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            disabled={busy}
                            aria-label={`${t('operators.column.actions')}: ${row.name ?? row.email}`}
                          >
                            <Icon name="ellipsis" size={15} />
                          </Button>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {load.state === 'loading' ? (
            <p role="status" className="sr-only">
              {t('operators.loading')}
            </p>
          ) : null}
        </div>
      )}

      <ActionDialog
        open={dialog?.kind === 'invite'}
        onClose={() => {
          setDialog(null);
        }}
        title={t('operators.invite.title')}
        description={t('operators.invite.lede')}
        cancelLabel={t('operators.cancel')}
        blockedText={failure ?? ''}
        confirm={{
          label: busy ? t('operators.invite.working') : t('operators.invite.confirm'),
          disabled: busy || !inviteOk,
          onClick: () => {
            write(
              CONTROL_PANEL_PATHS.operators,
              'POST',
              { email: email.trim(), name: name.trim() },
              { title: t('operators.done.invited'), description: email.trim() },
            );
          },
        }}
      >
        <DialogField label={t('operators.invite.name')} htmlFor="operator-invite-name">
          <input
            id="operator-invite-name"
            className={FIELD}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </DialogField>
        <DialogField label={t('operators.invite.email')} htmlFor="operator-invite-email">
          <input
            id="operator-invite-email"
            className={FIELD}
            value={email}
            placeholder={t('operators.invite.emailPlaceholder')}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </DialogField>
      </ActionDialog>

      {dialog?.kind === 'suspend' ? (
        <ActionDialog
          open
          onClose={() => {
            setDialog(null);
          }}
          title={t('operators.suspend.title', { who: dialog.row.name ?? dialog.row.email })}
          description={t('operators.suspend.lede')}
          tier={{ label: t('operators.suspend.tier'), tone: 'warn' }}
          cancelLabel={t('operators.cancel')}
          blockedText={failure ?? ''}
          confirm={{
            label: busy ? t('operators.working') : t('operators.suspend.confirm'),
            tone: 'warn',
            disabled: busy,
            onClick: () => {
              write(CONTROL_PANEL_PATHS.suspendOperator(dialog.row.id), 'POST', undefined, {
                title: t('operators.done.suspended'),
                description: dialog.row.name ?? dialog.row.email,
              });
            },
          }}
        />
      ) : null}

      {dialog?.kind === 'delete' ? (
        <ActionDialog
          open
          onClose={() => {
            setDialog(null);
          }}
          title={t('operators.delete.title', { who: dialog.row.name ?? dialog.row.email })}
          description={t('operators.delete.lede')}
          tier={{ label: t('danger.tier'), tone: 'bad' }}
          cancelLabel={t('operators.cancel')}
          blockedText={failure ?? ''}
          confirm={{
            label: busy ? t('operators.working') : t('operators.delete.confirm'),
            tone: 'destructive',
            disabled: busy || typed.trim().toLowerCase() !== dialog.row.email.toLowerCase(),
            onClick: () => {
              write(CONTROL_PANEL_PATHS.operator(dialog.row.id), 'DELETE', undefined, {
                title: t('operators.done.deleted'),
                description: dialog.row.email,
              });
            },
          }}
        >
          <DialogField
            label={t('operators.delete.type', { email: dialog.row.email })}
            htmlFor="operator-delete-email"
          >
            <input
              id="operator-delete-email"
              className={cn(FIELD, 'font-mono')}
              value={typed}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
              /* ⚠️ The address is printed one line above; a paste proves nothing about reading it. */
              onPaste={(event) => {
                event.preventDefault();
              }}
            />
          </DialogField>
        </ActionDialog>
      ) : null}
    </div>
  );
}

/** The mockup's menu per state — minus *reset second factor* (S-19). */
function menuFor(
  row: OperatorRosterEntry,
  standing: Standing,
  t: ReturnType<typeof useT>,
  on: {
    readonly reset: () => void;
    readonly suspend: () => void;
    readonly activate: () => void;
    readonly remove: () => void;
  },
): MenuItem[] {
  const first: MenuItem[] =
    standing === 'invited'
      ? [{ label: t('operators.action.resend'), icon: 'send-horizontal', onSelect: on.reset }]
      : standing === 'active'
        ? [{ label: t('operators.action.reset'), icon: 'key', onSelect: on.reset }]
        : [{ label: t('operators.action.activate'), icon: 'refresh-cw', onSelect: on.activate }];

  if (row.isYou) {
    return [...first, { type: 'separator' }, { type: 'label', label: t('operators.selfNote') }];
  }

  return [
    ...first,
    { type: 'separator' },
    ...(standing === 'active'
      ? [
          {
            label: t('operators.action.suspend'),
            icon: 'triangle-alert' as const,
            onSelect: on.suspend,
          },
        ]
      : []),
    { label: t('operators.action.delete'), icon: 'trash', destructive: true, onSelect: on.remove },
  ];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/u).filter(Boolean);
  return (
    parts
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('') || '?'
  ).toUpperCase();
}
